/** Block data model for the blog editor */

export type TextBlockType = 'paragraph' | 'lede' | 'h2' | 'h3' | 'h4' | 'blockquote';

/** A single item inside a Columns block — image, video, or rich text */
export type ColumnItem =
  | { kind: 'image'; src?: string; alt?: string; caption?: string }
  | { kind: 'video'; src?: string; poster?: string; caption?: string }
  | { kind: 'text'; text?: string }
  | { kind: 'icon'; src?: string; size?: number };

export type Block =
  | { id: string; type: TextBlockType; text: string }
  | { id: string; type: 'pullquote'; quote: string; cite?: string }
  | { id: string; type: 'ul' | 'ol'; items: string[] }
  | { id: string; type: 'code'; code: string; language?: string }
  | { id: string; type: 'hr' }
  | { id: string; type: 'BlogImage'; src?: string; alt?: string; caption?: string; breakout?: boolean; width?: string; align?: 'left' | 'center' | 'right'; aspectRatio?: string }
  | { id: string; type: 'ImageCarousel'; images: { src: string; alt: string }[]; caption?: string; filmstrip?: boolean; width?: string; align?: 'left' | 'center' | 'right' }
  | { id: string; type: 'ImageGrid'; images: { src: string; alt: string; caption?: string }[]; breakout?: boolean; width?: string; align?: 'left' | 'center' | 'right' }
  | { id: string; type: 'BlogVideo'; src?: string; poster?: string; caption?: string; breakout?: boolean; width?: string; align?: 'left' | 'center' | 'right'; aspectRatio?: string }
  | { id: string; type: 'Callout'; variant?: 'default' | 'note' | 'warning'; title?: string; children?: string }
  | { id: string; type: 'ReadingCallout'; eyebrow?: string; body?: string; linkLabel?: string; linkUrl?: string }
  | { id: string; type: 'BlogTable'; headers?: string[]; rows?: { cells: string[] }[]; highlightLast?: boolean }
  /* New blocks */
  | { id: string; type: 'Columns'; cols: 2 | 3; items: ColumnItem[]; caption?: string; breakout?: boolean }
  | { id: string; type: 'AuthorCard'; name?: string; role?: string; bio?: string; avatar?: string; profileUrl?: string }
  | { id: string; type: 'InlineCTA'; heading?: string; body?: string; buttonText?: string; buttonHref?: string }
  | { id: string; type: 'TLDR'; body?: string }
  | { id: string; type: 'StatsGrid'; items?: { value: string; label: string }[]; cols?: 2 | 3 | 4; variant?: 'default' | 'brand' | 'dark' }
  | { id: string; type: 'rawHtml'; html: string };

export interface PostAuthor {
  name: string;
  avatar?: string;
  profileUrl?: string;
}

export interface PostNewsletter {
  eyebrow?: string;
  heading?: string;
  body?: string;
  placeholder?: string;
  buttonText?: string;
}

export interface PostIcons {
  listen?: string;
  shareLinkedIn?: string;
  shareTwitter?: string;
  shareCopy?: string;
  backToTop?: string;
}

export interface PostMeta {
  title: string;
  category?: string;
  contentType?: string;
  /** Newsroom posts only — which /news category tab(s) this post belongs under. */
  newsroomType?: ('Announcements' | 'In the news' | 'Awards')[];
  dek?: string;
  heroImg?: string;
  heroAlt?: string;
  date?: string;
  authors: PostAuthor[];
  audioUrl?: string;
  audioTimings?: string;
  audioDuration?: string;
  newsletter?: PostNewsletter;
  icons?: PostIcons;
  tags?: { tag?: string }[];
  status?: 'draft' | 'published';
  /** When true, this post is pinned as the featured (hero) post on the /blogs listing page. */
  featured?: boolean;
  /** True when the post was created via the Claude push API without a logged-in
   * session. Used by the admin UI to show a "Generate Audio" CTA, since audio
   * is no longer auto-generated for Claude pushes (it's deferred until the
   * user requests it or until publish). */
  claudePushed?: boolean;
  /** URL to the original PDF resource (White Papers, Case Studies, etc.) */
  pdfUrl?: string;
  /** Total number of pages in the PDF */
  pdfPageCount?: number;
  /** Pre-generated thumbnail image URLs for the first N pages of the PDF */
  pdfPageImages?: string[];
  /** ISO date string for when the post was last updated */
  updatedAt?: string;
  /** Audit trail stamped when a post is created via Claude push or convert API */
  importInfo?: {
    via: string;
    importedBy: string;
    importedByEmail: string;
    role: string;
    importedAt: string;
    source: string;
    complianceScore: number;
  };
  /** Sub-brand this post belongs to. Lowercase slug: 'innovaccer' | 'gravity' | 'flow' | 'comet' | 'galaxy' | 'atlas'. */
  brand?: string;
  /** Custom heading above the body content on resource detail pages (e.g. "About this Whitepaper"). Defaults to "About this {category}" when absent. */
  bodyHeading?: string;
  // SEO / meta
  metaTitle?: string;
  metaDescription?: string;
  metaImage?: string;
  metaKeywords?: string;
  metaRobot?: string;
  /** Canonical URL on innovaccer.com for stub resource pages with no PDF and no body. */
  newsLink?: string;
  // CMS-only audit dates (NOT rendered on the public page)
  createdDate?: string;
  updatedDate?: string;
}

export interface PostData {
  slug: string;
  meta: PostMeta;
  blocks: Block[];
}

export const BLOCK_LABELS: Record<Block['type'], string> = {
  paragraph: 'Paragraph',
  lede: 'Lead Paragraph',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  blockquote: 'Block Quote',
  pullquote: 'Pull Quote',
  ul: 'Bulleted List',
  ol: 'Numbered List',
  code: 'Code Block',
  hr: 'Divider',
  BlogImage: 'Image',
  ImageCarousel: 'Image Carousel',
  ImageGrid: 'Image Grid',
  BlogVideo: 'Video',
  Callout: 'Callout',
  ReadingCallout: 'Reading Callout',
  BlogTable: 'Table',
  Columns: 'Columns (side-by-side)',
  AuthorCard: 'Author Card',
  InlineCTA: 'Inline CTA',
  TLDR: 'TL;DR Summary',
  StatsGrid: 'Stats Grid',
  rawHtml: 'HTML Embed',
};

export const BLOCK_GROUPS = [
  {
    label: 'Text',
    types: ['paragraph', 'lede', 'h2', 'h3', 'h4', 'blockquote', 'pullquote'] as Block['type'][],
  },
  {
    label: 'Lists & Code',
    types: ['ul', 'ol', 'code', 'hr'] as Block['type'][],
  },
  {
    label: 'Media',
    types: ['BlogImage', 'ImageCarousel', 'ImageGrid', 'BlogVideo', 'Columns'] as Block['type'][],
  },
  {
    label: 'Components',
    types: ['Callout', 'ReadingCallout', 'BlogTable', 'TLDR', 'InlineCTA', 'AuthorCard', 'StatsGrid', 'rawHtml'] as Block['type'][],
  },
];

export function defaultBlock(type: Block['type']): Block {
  const id = Math.random().toString(36).slice(2, 10);
  switch (type) {
    case 'paragraph': return { id, type: 'paragraph', text: '' };
    case 'lede':      return { id, type: 'lede', text: '' };
    case 'h2':        return { id, type: 'h2', text: '' };
    case 'h3':        return { id, type: 'h3', text: '' };
    case 'h4':        return { id, type: 'h4', text: '' };
    case 'blockquote':return { id, type: 'blockquote', text: '' };
    case 'pullquote': return { id, type: 'pullquote', quote: '', cite: '' };
    case 'ul':        return { id, type: 'ul', items: [''] };
    case 'ol':        return { id, type: 'ol', items: [''] };
    case 'code':      return { id, type: 'code', code: '', language: '' };
    case 'hr':        return { id, type: 'hr' };
    case 'BlogImage': return { id, type: 'BlogImage', src: '', alt: '', caption: '', breakout: false };
    case 'ImageCarousel': return { id, type: 'ImageCarousel', images: [], caption: '', filmstrip: false };
    case 'ImageGrid': return { id, type: 'ImageGrid', images: [], breakout: false };
    case 'BlogVideo': return { id, type: 'BlogVideo', src: '', poster: '', caption: '', breakout: false };
    case 'Callout':   return { id, type: 'Callout', variant: 'default', title: '', children: '' };
    case 'ReadingCallout': return { id, type: 'ReadingCallout', eyebrow: '', body: '', linkLabel: 'Read more', linkUrl: '' };
    case 'BlogTable': return { id, type: 'BlogTable', headers: ['Column 1', 'Column 2'], rows: [{ cells: ['', ''] }], highlightLast: false };
    case 'Columns':   return { id, type: 'Columns', cols: 2, items: [{ kind: 'image' }, { kind: 'image' }], caption: '', breakout: false };
    case 'AuthorCard':return { id, type: 'AuthorCard', name: '', role: '', bio: '', avatar: '', profileUrl: '' };
    case 'InlineCTA': return { id, type: 'InlineCTA', heading: '', body: '', buttonText: 'Learn more', buttonHref: '#' };
    case 'TLDR':      return { id, type: 'TLDR', body: '' };
    case 'StatsGrid': return { id, type: 'StatsGrid', items: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }], cols: 3, variant: 'default' };
    case 'rawHtml':   return { id, type: 'rawHtml', html: '' };
  }
}
